// Matches the interface in tempo devtools
export const STORYBOARD_TOP_CONSTANT = "STORYBOARD-TOP-CONSTANT";

/**
 * Used to uniquely identify an element in the DOM or a component in the nav tree.
 *
 * Created when building the nav tree
 */
export class TempoElement {
  public readonly codebaseId: string;
  public readonly storyboardId: string;
  public readonly uniquePath: string;

  private cachedKey: string | null;

  /**
   * If codebase ID is undefined then it doesn't exist in our codebase, but is still a valid lookup
   */
  constructor(
    codebaseId: string | undefined,
    storyboardId: string,
    uniquePath: string,
  ) {
    if (codebaseId && /[^0-9a-zA-Z-_]/.test(codebaseId)) {
      throw new Error("Codebase ID contains invalid chars :" + codebaseId);
    }

    if (storyboardId && /[^0-9a-zA-Z-_]/.test(storyboardId)) {
      throw new Error("Storyboard ID contains invalid chars :" + codebaseId);
    }

    this.codebaseId = codebaseId || "";
    this.storyboardId = storyboardId;
    this.uniquePath = uniquePath;
    this.cachedKey = null;
  }

  isEqual(other: TempoElement): boolean {
    return (
      this.codebaseId === other.codebaseId &&
      this.storyboardId === other.storyboardId &&
      this.uniquePath === other.uniquePath
    );
  }

  getKey(): string {
    if (this.cachedKey) {
      return this.cachedKey;
    }

    this.cachedKey = TempoElement.makeKey(
      this.codebaseId,
      this.storyboardId,
      this.uniquePath,
    );
    return this.cachedKey;
  }

  /**
   * Creates a TempoElement key from components without instantiating a TempoElement.
   * Use this when you have the components but don't need a full TempoElement instance.
   */
  static makeKey(
    codebaseId: string,
    storyboardId: string,
    uniquePath: string,
  ): string {
    return `TE_${codebaseId}_${storyboardId}_${uniquePath}`;
  }

  /**
   * Note, codebase ID is allowed to be empty but not the storyboard ID or unique path
   */
  isEmpty(): boolean {
    if (this.storyboardId && this.uniquePath) {
      return false;
    }

    return true;
  }

  static fromKey(key?: string | null): TempoElement {
    if (!key) {
      return TempoElement.empty();
    }

    // Account for the TE_ prefix
    const [_, codebaseId, storyboardId, uniquePath] = key.split("_");

    if (!storyboardId || !uniquePath) {
      return TempoElement.empty();
    }

    return new TempoElement(codebaseId, storyboardId, uniquePath);
  }

  static fromOtherElement(other: TempoElement): TempoElement {
    return new TempoElement(
      other.codebaseId,
      other.storyboardId,
      other.uniquePath,
    );
  }

  static empty(): TempoElement {
    return new TempoElement("", "", "");
  }

  /**
   * Returns a tempo element that can be used to represent the storyboard itself
   */
  static forStoryboard(storyboardId: string): TempoElement {
    return new TempoElement(STORYBOARD_TOP_CONSTANT, storyboardId, "0");
  }

  /**
   * If the storyboardId is passed in it checks if it is equal to this particular storyboard
   */
  isStoryboard(storyboardId?: string): boolean {
    return (
      this.codebaseId === STORYBOARD_TOP_CONSTANT &&
      (!storyboardId || this.storyboardId === storyboardId)
    );
  }

  /**
   * @returns if this element is inside a storyboard and known in the codebase
   */
  isKnownElement(): boolean {
    return (
      !this.isEmpty() &&
      Boolean(this.codebaseId) &&
      this.codebaseId !== STORYBOARD_TOP_CONSTANT
    );
  }

  isParentOf(other?: TempoElement): boolean {
    if (!other) {
      return false;
    }

    if (this.isStoryboard()) {
      return (
        this.storyboardId === other.storyboardId &&
        this.uniquePath !== other.uniquePath
      );
    }

    return (
      this.storyboardId === other.storyboardId &&
      this.uniquePath !== other.uniquePath &&
      Boolean(this.uniquePath) &&
      Boolean(other.uniquePath) &&
      other.uniquePath.startsWith(this.uniquePath)
    );
  }

  isDirectParentOf(other?: TempoElement): boolean {
    if (!other) {
      return false;
    }

    return (
      this.isParentOf(other) &&
      this.uniquePath.split("-").length + 1 ===
        other.uniquePath.split("-").length
    );
  }

  isSiblingOf(other?: TempoElement): boolean {
    if (!other || !this.uniquePath || !other.uniquePath) {
      return false;
    }

    if (this.isEqual(other)) {
      return false;
    }

    if (this.isStoryboard()) {
      return other.isStoryboard();
    }

    const pathUntilLastSegment = this.uniquePath
      .split("-")
      .slice(0, -1)
      .join("-");
    const otherPathUntilLastSegment = other.uniquePath
      .split("-")
      .slice(0, -1)
      .join("-");

    return (
      this.storyboardId === other.storyboardId &&
      this.uniquePath !== other.uniquePath &&
      Boolean(pathUntilLastSegment) &&
      Boolean(otherPathUntilLastSegment) &&
      pathUntilLastSegment === otherPathUntilLastSegment
    );
  }
}
